import { useEffect, useState } from "react";
import { Pencil } from "lucide-react";
import { toast } from "sonner";
import { web, errMsg } from "../../lib/api";
import { Btn } from "./kit";
import { Modal, Field } from "./Form";

export const ARCHETYPES = [
  ["Floor General", "Runs the offense, controls tempo, and gets teammates involved."],
  ["Playmaking Guard", "Creates scoring opportunities through passing, penetration, and ball handling."],
  ["Scoring Guard", "Generates points through drives, pull-up jumpers, and perimeter shooting."],
  ["Sharpshooter", "Excels at outside shooting, spacing the floor, and catch-and-shoot opportunities."],
  ["Slasher", "Attacks the basket, finishes through contact, and draws fouls."],
  ["Three-Level Scorer", "Scores effectively at the rim, from midrange, and beyond the arc."],
  ["Shot Creator", "Uses footwork, dribbling, and separation to create shots against defenders."],
  ["3-and-D Wing", "Combines reliable three-point shooting with strong perimeter defense."],
  ["Two-Way Wing", "Contributes offensively while defending strong opposing scorers."],
  ["Point Forward", "Initiates the offense and creates for teammates from a forward position."],
  ["Lockdown Defender", "Specializes in containing opponents and disrupting their offense."],
  ["Versatile Defender", "Guards multiple positions and switches effectively across matchups."],
  ["Athletic Finisher", "Scores through cuts, transition runs, lobs, and explosive finishes."],
  ["Stretch Big", "Pulls interior defenders away from the basket with perimeter shooting."],
  ["Post Scorer", "Creates offense near the basket using positioning, footwork, and touch."],
  ["Rim Runner", "Sprints the floor, sets screens, and rolls hard to the basket for finishes."],
  ["Rim Protector", "Deters and contests shots near the basket while anchoring interior defense."],
  ["Rebounding Specialist", "Secures defensive rebounds and creates second-chance opportunities."],
  ["Playmaking Big", "Facilitates offense through handoffs, passing, and decisions from the post or elbow."],
  ["Glue Player", "Helps the team through smart decisions, screening, hustle, and supporting teammates."],
];
const DESC = Object.fromEntries(ARCHETYPES);

const Badge = ({ label, name, primary }) => (
  <div data-testid={`archetype-${primary ? "primary" : "secondary"}`} className={`min-w-0 flex-1 rounded-xl border px-4 py-3 ${primary ? "border-[#39FF14]/50 bg-[#39FF14]/10" : "border-[#1E293B] bg-[#162032]"}`}>
    <p className="font-mono-gd text-[10px] uppercase tracking-[0.2em] text-slate-400">{label}</p>
    <p className={`font-display text-xl font-bold uppercase ${primary ? "text-[#39FF14]" : "text-white"}`}>{name}</p>
    <p className="text-xs text-slate-300">{DESC[name]}</p>
  </div>
);

/** Shows a player's primary/secondary archetype; coaches get an edit button. */
export const Archetypes = ({ p, canEdit, onEdit }) => {
  if (!p.primary_archetype && !canEdit) return null;
  return (
    <section data-testid="archetypes" className="mb-6">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="font-mono-gd text-xs uppercase tracking-[0.2em] text-slate-400">Archetype</h2>
        {canEdit && <button data-testid="edit-archetypes-button" onClick={onEdit} className="no-print inline-flex items-center gap-1.5 text-xs font-semibold text-[#39FF14] hover:underline"><Pencil className="h-3.5 w-3.5" />{p.primary_archetype ? "Change" : "Set archetypes"}</button>}
      </div>
      {p.primary_archetype ? (
        <div className="flex flex-col gap-3 sm:flex-row">
          <Badge label="Primary" name={p.primary_archetype} primary />
          {p.secondary_archetype && <Badge label="Secondary" name={p.secondary_archetype} />}
        </div>
      ) : <p className="text-sm text-slate-400">No archetype set yet.</p>}
    </section>
  );
};

const Select = ({ value, onChange, testid, exclude, allowNone }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full rounded-xl border border-[#1E293B] bg-[#0F172A] px-3.5 py-2.5 text-sm text-white">
    <option value="">{allowNone ? "None" : "Choose…"}</option>
    {ARCHETYPES.filter(([n]) => n !== exclude).map(([n]) => <option key={n} value={n}>{n}</option>)}
  </select>
);

export const ArchetypeEditor = ({ open, onClose, tid, p, onSaved }) => {
  const [primary, setPrimary] = useState("");
  const [secondary, setSecondary] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPrimary(p.primary_archetype ?? ""); setSecondary(p.secondary_archetype ?? ""); } }, [open, p]);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await web.put(`/teams/${tid}/players/${p.id}/archetypes`, { primary: primary || null, secondary: (primary && secondary) || null });
      toast.success("Archetypes saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose} testid="archetype-dialog" title="Player archetypes" description="Recruiters see these on the prospect card.">
      <form onSubmit={save} className="mt-2 space-y-4">
        <Field label="Primary archetype"><Select testid="archetype-primary-select" value={primary} onChange={(v) => { setPrimary(v); if (v === secondary) setSecondary(""); }} allowNone /></Field>
        {primary && <p className="-mt-2 text-xs text-slate-300">{DESC[primary]}</p>}
        <Field label="Secondary archetype"><Select testid="archetype-secondary-select" value={secondary} onChange={setSecondary} exclude={primary} allowNone /></Field>
        {secondary && <p className="-mt-2 text-xs text-slate-300">{DESC[secondary]}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Btn type="button" variant="ghost" onClick={onClose} data-testid="archetype-cancel">Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="archetype-save">{busy ? "Saving…" : "Save"}</Btn>
        </div>
      </form>
    </Modal>
  );
};
